/**
 * useFocusHeadingOnNavigate — move focus to a page's own <h1> when arriving
 * via a navigation that asked for it, then forgets the request.
 *
 * Pairs with a `navigate(to, { state: { focusHeading: true } })` call
 * elsewhere: a route change alone leaves focus wherever it was, which strands
 * keyboard and screen-reader users with no cue they landed anywhere new. The
 * heading needs `tabIndex={-1}` on the returned ref's element to be focusable
 * at all, since headings aren't natively interactive.
 */
import { useEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavigationFocusState {
  focusHeading?: boolean;
}

export function useFocusHeadingOnNavigate<T extends HTMLElement>(): RefObject<T> {
  const ref = useRef<T>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const shouldFocus = Boolean((location.state as NavigationFocusState | null)?.focusHeading);

  useEffect(() => {
    if (!shouldFocus) return;
    ref.current?.focus();
    // Clear the flag via `replace` so navigating back into this entry later
    // doesn't refocus it.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [shouldFocus, location.pathname, location.search, navigate]);

  return ref;
}
